/* eslint-disable react/prop-types */
import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { styles } from '../FaceReportStyles';
import StyleFieldChart from '../../../components/StyleFieldChart';
import { useTranslation } from 'react-i18next';

const getFeasibilityCopy = (mode, t) => {
    if (mode === 'SUCCESS') {
        return {
            title: t('face_result.detail.visual.completion'),
            badge: t('face_result.detail.visual.success_badge'),
            body: t('face_result.detail.visual.success_body'),
        };
    }

    if (mode === 'POTENTIAL') {
        return {
            title: t('face_result.detail.visual.direction'),
            badge: t('face_result.detail.visual.potential_badge'),
            body: t('face_result.detail.visual.potential_body'),
        };
    }

    return {
        title: t('face_result.detail.visual.alternative'),
        badge: t('face_result.detail.visual.challenge_badge'),
        body: t('face_result.detail.visual.challenge_body'),
    };
};

const getEvidenceCards = (evidenceList, t) => {
    return (Array.isArray(evidenceList) ? evidenceList : [])
        .filter((item) => item?.feature || item?.desc)
        .map((item, index) => ({
            key: `${item.feature || 'feature'}-${index}`,
            feature: item.feature || t('face_result.detail.visual.metric'),
            label: item.type === 'High'
                ? t('face_result.detail.visual.strong_effect')
                : item.type === 'Balanced'
                    ? t('face_result.detail.visual.balanced_effect')
                    : t('face_result.detail.visual.soft_effect'),
            desc: item.desc || t('face_result.detail.visual.metric_description'),
            comment: item.comment || t('face_result.detail.visual.metric_comment'),
        }));
};

const VisualDNASection = ({ styleFieldData, matchInsight, masculinityPercentile, evidenceList, compact = false }) => {
    const { t } = useTranslation();
    const [showAllEvidence, setShowAllEvidence] = useState(false);

    const evidenceCards = useMemo(() => getEvidenceCards(evidenceList, t), [evidenceList, t]);
    const visibleEvidence = compact || !showAllEvidence ? evidenceCards.slice(0, 2) : evidenceCards;

    const safePercentile = Number.isFinite(Number(masculinityPercentile))
        ? Number(masculinityPercentile).toFixed(1)
        : '-';

    const feasibilityCopy = styleFieldData?.feasibilityMode
        ? getFeasibilityCopy(styleFieldData.feasibilityMode, t)
        : null;
    const hasGoal = Boolean(styleFieldData?.targetCoord);

    if (!styleFieldData && evidenceCards.length === 0 && safePercentile === '-') {
        return null;
    }

    return (
        <div style={styles.section}>
            {feasibilityCopy ? (
                <div style={heroInfoCardStyle}>
                    <div style={eyebrowStyle}>{feasibilityCopy.title}</div>
                    <div style={headlineStyle}>{feasibilityCopy.badge}</div>
                    <p style={bodyStyle}>{feasibilityCopy.body}</p>
                </div>
            ) : null}

            {matchInsight ? (
                <div style={summaryCardStyle}>
                    <div style={eyebrowStyle}>{t('face_result.detail.visual.one_line')}</div>
                    <div style={headlineStyle}>
                        {matchInsight.emoji} {matchInsight.title || t('face_result.detail.visual.insight_fallback')}
                    </div>
                    <p style={bodyStyle}>{matchInsight.body || t('face_result.detail.visual.insight_body_fallback')}</p>
                </div>
            ) : null}

            {styleFieldData ? (
                <div style={summaryCardStyle}>
                    <div style={eyebrowStyle}>{t('face_result.detail.visual.coordinates')}</div>
                    <p style={{ ...bodyStyle, marginBottom: '1rem' }}>
                        {hasGoal
                            ? t('face_result.detail.visual.coordinates_with_goal')
                            : t('face_result.detail.visual.coordinates_without_goal')}
                    </p>
                    <StyleFieldChart
                        radii={styleFieldData.radii}
                        targetCoord={styleFieldData.targetCoord}
                        identityDot={styleFieldData.identityDot}
                    />
                </div>
            ) : null}

            {evidenceCards.length > 0 ? (
                <div style={summaryCardStyle}>
                    <div style={eyebrowStyle}>{t('face_result.detail.slides.evidence')}</div>
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                        {visibleEvidence.map((item) => (
                            <motion.div
                                key={item.key}
                                initial={{ opacity: 0, y: 8 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2 }}
                                viewport={{ once: true }}
                                style={evidenceCardStyle}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                                    <div style={{ color: '#f8fafc', fontWeight: 700, fontSize: '0.9rem' }}>{item.feature}</div>
                                    <div style={evidenceBadgeStyle}>{item.label}</div>
                                </div>
                                <div style={{ color: '#e2e8f0', fontSize: '0.88rem', marginTop: '0.45rem' }}>
                                    {item.desc}
                                </div>
                                <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginTop: '0.35rem', lineHeight: 1.55 }}>
                                    {item.comment}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                    {!compact && evidenceCards.length > 2 ? (
                        <button
                            type="button"
                            onClick={() => setShowAllEvidence((prev) => !prev)}
                            style={toggleButtonStyle}
                        >
                            {showAllEvidence
                                ? t('face_result.detail.visual.show_key_evidence')
                                : t('face_result.detail.visual.show_all_evidence', { count: evidenceCards.length })}
                        </button>
                    ) : null}
                </div>
            ) : null}

            {safePercentile !== '-' ? (
                <div style={summaryCardStyle}>
                    <div style={eyebrowStyle}>{t('face_result.ui.masculinity_title')}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.8rem' }}>
                        <div style={barTrackStyle}>
                            <div style={{ ...barFillStyle, width: `${safePercentile}%` }} />
                        </div>
                        <div style={{ minWidth: '72px', color: '#f8fafc', fontWeight: 800 }}>{safePercentile}%</div>
                    </div>
                    <p style={{ ...bodyStyle, marginTop: '0.75rem' }}>
                        {t('face_result.detail.visual.masculinity_description')}
                    </p>
                </div>
            ) : null}
        </div>
    );
};

const heroInfoCardStyle = {
    background: 'rgba(59,130,246,0.08)',
    border: '1px solid rgba(96,165,250,0.16)',
    borderRadius: '18px',
    padding: '1rem',
    marginBottom: '1rem',
};

const summaryCardStyle = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '18px',
    padding: '1rem',
    marginBottom: '1rem',
};

const eyebrowStyle = {
    fontSize: '0.78rem',
    color: '#60a5fa',
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: '0.45rem',
};

const headlineStyle = {
    color: '#f8fafc',
    fontWeight: 800,
    fontSize: '1rem',
    lineHeight: 1.4,
};

const bodyStyle = {
    color: '#cbd5e1',
    fontSize: '0.88rem',
    lineHeight: 1.65,
    margin: '0.45rem 0 0 0',
};

const evidenceCardStyle = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '14px',
    padding: '0.95rem',
};

const evidenceBadgeStyle = {
    background: 'rgba(96,165,250,0.12)',
    color: '#93c5fd',
    borderRadius: '999px',
    padding: '0.2rem 0.55rem',
    fontSize: '0.72rem',
    fontWeight: 700,
};

const toggleButtonStyle = {
    marginTop: '0.85rem',
    width: '100%',
    border: 'none',
    borderRadius: '12px',
    padding: '0.75rem',
    background: 'rgba(255,255,255,0.04)',
    color: '#93c5fd',
    cursor: 'pointer',
    fontWeight: 700,
};

const barTrackStyle = {
    flex: 1,
    height: '12px',
    background: 'rgba(255,255,255,0.08)',
    borderRadius: '999px',
    overflow: 'hidden',
};

const barFillStyle = {
    height: '100%',
    borderRadius: '999px',
    background: 'linear-gradient(90deg, #60a5fa, #a78bfa)',
};

export default VisualDNASection;
